import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { apiFetch } from '../../../utils/api';

function SupplierDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [supplier, setSupplier] = useState(null);
  const [editedSupplier, setEditedSupplier] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    fetchSupplier();
  }, [id]);

  const fetchSupplier = async () => {
    try {
      const res = await apiFetch(`/api/suppliers/${id}`);
      const data = await res.json();
      setSupplier(data);
      setEditedSupplier(data);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching supplier:', err);
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await apiFetch(`/api/suppliers/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(editedSupplier)
      });
      
      if (res.ok) {
        const updated = await res.json();
        setSupplier(updated);
        setEditedSupplier(updated);
        alert('Supplier updated successfully!');
      } else {
        alert('Failed to update supplier');
      }
    } catch (err) {
      console.error('Error saving supplier:', err);
      alert('Error saving supplier');
    }
    setSaving(false);
  };

  const handleDelete = async () => {
    if (!window.confirm(`Are you sure you want to delete supplier "${supplier.company_name}"? This action cannot be undone.`)) {
      return;
    }

    setDeleting(true);
    try {
      const res = await apiFetch(`/api/suppliers/${id}`, {
        method: 'DELETE'
      });
      
      if (res.ok) {
        alert('Supplier deleted successfully!');
        navigate('/suppliers');
      } else {
        alert('Failed to delete supplier');
      }
    } catch (err) {
      console.error('Error deleting supplier:', err);
      alert('Error deleting supplier');
    }
    setDeleting(false);
  };

  if (loading || !supplier || !editedSupplier) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  const tabs = [
    { id: 'overview', label: 'Overview' },
    { id: 'orders', label: 'Orders', icon: '📦' },
    { id: 'invoices', label: 'Invoices', icon: '💳' },
    { id: 'products', label: 'Products', icon: '🔧' },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white shadow-md sticky top-0 z-10" style={{ height: '116px' }}>
        <div className="max-w-full mx-auto px-6 h-full flex flex-col justify-center">
          <div className="flex items-center justify-between">
            <button
              onClick={() => navigate('/suppliers')}
              className="text-white hover:text-blue-100 transition text-lg font-semibold"
            >
              ⬅ Back
            </button>
            <div className="flex-1 text-center">
              <h1 className="text-4xl font-bold">{supplier.company_name}</h1>
              <span className="text-sm text-blue-100">{supplier.supplier_code ? `Code: ${supplier.supplier_code}` : 'Supplier'}</span>
            </div>
            <div className="flex items-center space-x-2">
              <button 
                onClick={handleDelete}
                disabled={deleting}
                className="px-3 py-1.5 bg-red-600 hover:bg-red-700 text-white rounded text-sm font-medium transition disabled:opacity-50"
              >
                {deleting ? '⏳' : '🗑️'}
              </button>
              <button 
                onClick={handleSave}
                disabled={saving}
                className="px-4 py-2 bg-gray-300 hover:bg-gray-400 text-black rounded-lg font-medium transition text-sm shadow-sm disabled:opacity-50"
              >
                {saving ? '⏳ Saving...' : '💾 Save'}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="max-w-full px-4 py-3">
        <div className="bg-gray-50 rounded-xl shadow-sm p-2 border border-gray-200 inline-flex space-x-1">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-6 py-2.5 rounded-lg text-sm font-semibold transition-all ${
                activeTab === tab.id
                  ? 'bg-blue-600 text-white shadow-md'
                  : 'bg-gray-200 text-gray-600 hover:bg-gray-300 hover:text-gray-900'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Main Content */}
      <div className="px-4 pb-3">
        <div className="rounded-xl bg-gray-100 border-4 border-gray-400 shadow-[inset_6px_6px_12px_rgba(0,0,0,0.3),inset_-4px_-4px_8px_rgba(255,255,255,0.9)] overflow-auto max-h-[calc(100vh-135px)] min-h-[650px]">
          
          {/* Overview Tab */}
          {activeTab === 'overview' && (
            <div className="p-8 pb-12">
              <h2 className="text-xl font-semibold text-gray-900 mb-6">Supplier Information</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {/* Left Column */}
                <div className="space-y-5">
                  <div>
                    <label className="field-label mb-2 block">Supplier Name *</label>
                    <input
                      type="text"
                      value={editedSupplier?.company_name || ''}
                      onChange={(e) => setEditedSupplier({...editedSupplier, company_name: e.target.value})}
                      className="w-full bg-gray-50 border-2 border-gray-300 shadow-[inset_2px_2px_4px_rgba(0,0,0,0.15)] rounded px-3 py-2 data-value text-lg focus:ring-2 focus:ring-purple-500"
                    />
                  </div>
                  
                  <div>
                    <label className="field-label mb-2 block">Supplier Code</label>
                    <input
                      type="text"
                      value={editedSupplier?.supplier_code || ''}
                      onChange={(e) => setEditedSupplier({...editedSupplier, supplier_code: e.target.value})}
                      className="w-full bg-gray-50 border-2 border-gray-300 shadow-[inset_2px_2px_4px_rgba(0,0,0,0.15)] rounded px-3 py-2 data-value font-mono focus:ring-2 focus:ring-purple-500"
                      placeholder="e.g., SUP001"
                    />
                  </div>

                  <div>
                    <label className="field-label mb-2 block">Contact Name</label>
                    <input
                      type="text"
                      value={editedSupplier?.contact_name || ''}
                      onChange={(e) => setEditedSupplier({...editedSupplier, contact_name: e.target.value})}
                      className="w-full bg-gray-50 border-2 border-gray-300 shadow-[inset_2px_2px_4px_rgba(0,0,0,0.15)] rounded px-3 py-2 data-value focus:ring-2 focus:ring-purple-500"
                    />
                  </div>

                  <div>
                    <label className="field-label mb-2 block">Contact Details</label>
                    <div className="space-y-2">
                      <input
                        type="email"
                        value={editedSupplier?.email || ''}
                        onChange={(e) => setEditedSupplier({...editedSupplier, email: e.target.value})}
                        className="w-full bg-gray-50 border-2 border-gray-300 shadow-[inset_2px_2px_4px_rgba(0,0,0,0.15)] rounded px-3 py-2 data-value focus:ring-2 focus:ring-purple-500"
                        placeholder="📧 Contact Email"
                      />
                      <input
                        type="email"
                        value={editedSupplier?.ordering_email || ''}
                        onChange={(e) => setEditedSupplier({...editedSupplier, ordering_email: e.target.value})}
                        className="w-full bg-gray-50 border-2 border-gray-300 shadow-[inset_2px_2px_4px_rgba(0,0,0,0.15)] rounded px-3 py-2 data-value focus:ring-2 focus:ring-purple-500"
                        placeholder="📧 Ordering Email"
                      />
                      <input
                        type="tel"
                        value={editedSupplier?.phone || ''}
                        onChange={(e) => setEditedSupplier({...editedSupplier, phone: e.target.value})}
                        className="w-full bg-gray-50 border-2 border-gray-300 shadow-[inset_2px_2px_4px_rgba(0,0,0,0.15)] rounded px-3 py-2 data-value focus:ring-2 focus:ring-purple-500"
                        placeholder="📞 Telephone"
                      />
                      <input
                        type="tel"
                        value={editedSupplier?.mobile || ''}
                        onChange={(e) => setEditedSupplier({...editedSupplier, mobile: e.target.value})}
                        className="w-full bg-gray-50 border-2 border-gray-300 shadow-[inset_2px_2px_4px_rgba(0,0,0,0.15)] rounded px-3 py-2 data-value focus:ring-2 focus:ring-purple-500"
                        placeholder="📱 Mobile"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="field-label mb-2 block">Website</label>
                    <input
                      type="url"
                      value={editedSupplier?.website || ''}
                      onChange={(e) => setEditedSupplier({...editedSupplier, website: e.target.value})}
                      className="w-full bg-gray-50 border-2 border-gray-300 shadow-[inset_2px_2px_4px_rgba(0,0,0,0.15)] rounded px-3 py-2 data-value focus:ring-2 focus:ring-purple-500"
                      placeholder="https://..."
                    />
                  </div>
                </div>

                {/* Right Column */}
                <div className="space-y-5">
                  <div>
                    <label className="field-label mb-2 block">Address</label>
                    <div className="space-y-2">
                      <input
                        type="text"
                        value={editedSupplier?.address || ''}
                        onChange={(e) => setEditedSupplier({...editedSupplier, address: e.target.value})}
                        className="w-full bg-gray-50 border-2 border-gray-300 shadow-[inset_2px_2px_4px_rgba(0,0,0,0.15)] rounded px-3 py-2 data-value focus:ring-2 focus:ring-purple-500"
                        placeholder="Street address"
                      />
                      <input
                        type="text"
                        value={editedSupplier?.city || ''}
                        onChange={(e) => setEditedSupplier({...editedSupplier, city: e.target.value})}
                        className="w-full bg-gray-50 border-2 border-gray-300 shadow-[inset_2px_2px_4px_rgba(0,0,0,0.15)] rounded px-3 py-2 data-value focus:ring-2 focus:ring-purple-500"
                        placeholder="City"
                      />
                      <input
                        type="text"
                        value={editedSupplier?.county || ''}
                        onChange={(e) => setEditedSupplier({...editedSupplier, county: e.target.value})}
                        className="w-full bg-gray-50 border-2 border-gray-300 shadow-[inset_2px_2px_4px_rgba(0,0,0,0.15)] rounded px-3 py-2 data-value focus:ring-2 focus:ring-purple-500"
                        placeholder="County"
                      />
                      <input
                        type="text"
                        value={editedSupplier?.postcode || ''}
                        onChange={(e) => setEditedSupplier({...editedSupplier, postcode: e.target.value})}
                        className="w-full bg-gray-50 border-2 border-gray-300 shadow-[inset_2px_2px_4px_rgba(0,0,0,0.15)] rounded px-3 py-2 data-value font-semibold focus:ring-2 focus:ring-purple-500"
                        placeholder="Postcode"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="field-label mb-2 block">Financial Details</label>
                    <div className="space-y-2">
                      <input
                        type="number"
                        step="0.01"
                        value={editedSupplier?.credit_limit || ''}
                        onChange={(e) => setEditedSupplier({...editedSupplier, credit_limit: e.target.value})}
                        className="w-full bg-gray-50 border-2 border-gray-300 shadow-[inset_2px_2px_4px_rgba(0,0,0,0.15)] rounded px-3 py-2 data-value focus:ring-2 focus:ring-purple-500"
                        placeholder="💰 Credit Limit"
                      />
                      <input
                        type="text"
                        value={editedSupplier?.purchase_terms || ''}
                        onChange={(e) => setEditedSupplier({...editedSupplier, purchase_terms: e.target.value})}
                        className="w-full bg-gray-50 border-2 border-gray-300 shadow-[inset_2px_2px_4px_rgba(0,0,0,0.15)] rounded px-3 py-2 data-value focus:ring-2 focus:ring-purple-500"
                        placeholder="📋 Purchase Terms (e.g., Net 30)"
                      />
                      <input
                        type="text"
                        value={editedSupplier?.vat_number || ''}
                        onChange={(e) => setEditedSupplier({...editedSupplier, vat_number: e.target.value})}
                        className="w-full bg-gray-50 border-2 border-gray-300 shadow-[inset_2px_2px_4px_rgba(0,0,0,0.15)] rounded px-3 py-2 data-value focus:ring-2 focus:ring-purple-500"
                        placeholder="VAT Number"
                      />
                      <input
                        type="text"
                        value={editedSupplier?.account_reference || ''}
                        onChange={(e) => setEditedSupplier({...editedSupplier, account_reference: e.target.value})}
                        className="w-full bg-gray-50 border-2 border-gray-300 shadow-[inset_2px_2px_4px_rgba(0,0,0,0.15)] rounded px-3 py-2 data-value focus:ring-2 focus:ring-purple-500"
                        placeholder="Account Reference"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="field-label mb-2 block">Notes</label>
                    <textarea
                      value={editedSupplier?.notes || ''}
                      onChange={(e) => setEditedSupplier({...editedSupplier, notes: e.target.value})}
                      rows="4"
                      className="w-full bg-gray-50 border-2 border-gray-300 shadow-[inset_2px_2px_4px_rgba(0,0,0,0.15)] rounded px-3 py-2 data-value focus:ring-2 focus:ring-purple-500"
                      placeholder="Internal notes about this supplier..."
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Coming Soon Tabs */}
          {activeTab === 'orders' && (
            <div className="p-8 text-center py-12">
              <div className="text-6xl mb-4">📦</div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Orders</h3>
              <p className="text-gray-500">Purchase order management coming soon</p>
            </div>
          )}

          {activeTab === 'invoices' && (
            <div className="p-8 text-center py-12">
              <div className="text-6xl mb-4">💳</div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Invoices</h3>
              <p className="text-gray-500">Supplier invoice tracking coming soon</p>
            </div>
          )}

          {activeTab === 'products' && (
            <div className="p-8 text-center py-12">
              <div className="text-6xl mb-4">🔧</div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Products</h3>
              <p className="text-gray-500">Product catalog from this supplier coming soon</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default SupplierDetail;
